import * as React from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { DOM_NODES_IDS } from 'config/domNodesIds';
import { DraggableKinds } from 'config/draggableKinds';
import { mergeRefs } from 'utils/mergeRefs';

interface DraggableTableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
  draggable?: boolean;
  rowId: string;
}

/** Компонент для строки в таблице, которая будет сортироваться с помощью drag-n-drop */
const DraggableTableRow = ({
  index,
  moveRow,
  className,
  rowId,
  draggable = true,
  ...restProps
}: DraggableTableRowProps) => {
  const [, drag, preview] = useDrag({
    type: DraggableKinds.EDIT_FORM_TABLE_ROW_HANDLE,
    item: { index },
    collect: (monitor) => {
      return {
        isDragging: monitor.isDragging(),
      };
    },
  });

  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: DraggableKinds.EDIT_FORM_TABLE_ROW_HANDLE,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};

      if (dragIndex === index) {
        return {};
      }

      return {
        isOver: monitor.isOver(),
        dropClassName:
          dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: { index: number }) => {
      moveRow(item.index, index);
    },
  });

  React.useEffect(() => {
    if (!rowId) {
      return;
    }

    const element = document.querySelector(
      `#${DOM_NODES_IDS.EDIT_FORM_TABLE_ROW_DRAG_HANDLER(rowId)}`
    );

    if (element) {
      drag(element);
    }
  }, [drag, rowId]);

  if (!draggable) {
    return <tr {...restProps} />;
  }

  return (
    <tr
      ref={mergeRefs(preview, drop)}
      className={`${className}${isOver ? dropClassName : ''}`}
      {...restProps}
    />
  );
};

export default DraggableTableRow;
